// Layer 4 (UI) — the sign-off matrix (locked comp
// `.impeccable/mocks/decision/documents.html` B): nine people down, nine
// documents across, each cell current (a quiet tick), signed an older revision
// or unsigned past due (the breach tone), or unsigned with its due date ahead
// (a dashed date). Paul's per-person columns finish each row and the footer
// counts the current sign-offs per document. Renders
// `TrainingCompliance.transform.ts`; derives nothing.

import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { DowntimeCard, RankBar } from '../downtime/DowntimeCard';
import { TrainingCurrency } from './TrainingCurrency';
import { CAPS } from './documents-ui';
import type { SignoffCell, SignoffMatrix as SignoffMatrixRead } from './TrainingCompliance.transform';

/** A compact meter (comp `.meter`): the bar with the 90% target marker, the figure right. */
export function ComplianceMeter({ percent, tone }: { percent: number; tone: 'ok' | 'warn' | 'bad' }) {
  return (
    <span className="grid grid-cols-[minmax(0,1fr)_38px] items-center gap-2">
      <span className="relative block">
        <RankBar ratio={percent / 100} tone={tone} />
        <span aria-hidden className="absolute inset-y-0 left-[90%] w-[1.5px] bg-[var(--ink-primary)]" />
      </span>
      <span className="text-right text-[12.5px] font-semibold tabular-nums text-ink-primary">{percent}%</span>
    </span>
  );
}

const MARK_BAD =
  'inline-block whitespace-nowrap rounded-[5px] bg-[color-mix(in_srgb,var(--state-blocked)_9%,transparent)] px-[2.5px] text-[10px] font-semibold leading-[1.35] text-[var(--state-blocked)]';
const MARK_DUE =
  'inline-block whitespace-nowrap rounded-[5px] border border-dashed border-[var(--hairline-strong)] px-[2.5px] text-[10px] font-medium leading-[1.35] text-muted';

function CellMark({ cell }: { cell: SignoffCell }) {
  switch (cell.state) {
    case 'current':
      return <span className="text-xs text-muted" aria-label="current">✓</span>;
    case 'stale':
      return <span className={MARK_BAD} title={`Stale · ${cell.documentId} ${cell.signedVersion}`}>{cell.signedVersion}</span>;
    case 'overdue':
      return <span className={MARK_BAD} title={`Unsigned · ${cell.documentId} · due ${cell.dueLabel}`}>{cell.dueLabel}</span>;
    case 'due':
      return <span className={MARK_DUE} title={`Unsigned · ${cell.documentId} · due ${cell.dueLabel}`}>{cell.dueLabel}</span>;
    default:
      return null;
  }
}

const TD = 'h-[34px] border-b border-[var(--color-divider)] px-1 align-middle';

export function SignoffMatrix({
  matrix,
  onSelectDocument,
}: {
  matrix: SignoffMatrixRead;
  onSelectDocument: (id: string) => void;
}) {
  return (
    <Commentable name="SignoffMatrix">
      <DowntimeCard
        title="Sign-offs by person and document"
        className="flex h-full flex-col px-0 pb-0 pt-3 [&>header]:mb-0 [&>header]:border-b [&>header]:border-[var(--hairline)] [&>header]:px-4 [&>header]:pb-2.5"
      >
        <table className="w-full table-fixed border-collapse text-[12.5px] tabular-nums" data-signoff-matrix>
          <colgroup>
            <col className="w-[140px]" />
            <col className="w-[38px]" />
            {matrix.columns.map((column) => (
              <col key={column.id} />
            ))}
            <col className="w-[90px]" />
            <col className="w-[64px]" />
          </colgroup>
          <TrainingCurrency matrix={matrix} onSelectDocument={onSelectDocument} />
          <tbody>
            {matrix.rows.map((row) => (
              <tr key={row.personId} data-matrix-row={row.personId}>
                <td className={cn(TD, 'overflow-hidden pl-4 pr-0')}>
                  <span className="block truncate font-medium leading-tight text-ink-primary">{row.name}</span>
                  <span className="block text-[11px] leading-tight text-muted">{row.role}</span>
                </td>
                <td className={cn(TD, 'px-px text-center text-ink-secondary')}>{row.panel}</td>
                {row.cells.map((cell, index) => (
                  <td
                    key={cell.documentId}
                    data-matrix-cell={`${row.personId}:${cell.documentId}`}
                    data-state={cell.state}
                    className={cn(TD, 'px-0 text-center', matrix.columns[index]!.groupStart ? 'border-l border-l-[var(--color-divider)]' : '')}
                  >
                    <CellMark cell={cell} />
                  </td>
                ))}
                <td className={cn(TD, 'border-l border-l-[var(--color-divider)] pl-1 pr-1')}>
                  <ComplianceMeter percent={row.compliancePercent} tone={row.complianceTone} />
                </td>
                <td className={cn(TD, 'pr-2 text-right font-semibold text-ink-primary')}>{row.overdueItems}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr data-matrix-footer>
              <td colSpan={2} className={cn(CAPS, 'h-[30px] border-t border-[var(--hairline-strong)] pl-4 text-left')}>Current</td>
              {matrix.columns.map((column) => (
                <td
                  key={column.id}
                  data-matrix-total={column.id}
                  className={cn(
                    'h-[30px] border-t border-[var(--hairline-strong)] text-center text-[11.5px] text-muted',
                    column.groupStart ? 'border-l border-l-[var(--color-divider)]' : '',
                  )}
                >
                  <span className="font-semibold text-ink-primary">{column.current}</span>/{column.required}
                </td>
              ))}
              <td className="border-l border-t border-[var(--hairline-strong)] border-l-[var(--color-divider)]" />
              <td className="border-t border-[var(--hairline-strong)]" />
            </tr>
          </tfoot>
        </table>
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 border-t border-[var(--hairline)] px-4 pb-2.5 pt-2 text-[11.5px] text-muted" data-matrix-legend>
          <span className="inline-flex items-center gap-1.5"><span className="text-xs">✓</span>current</span>
          {matrix.legend.stale ? (
            <span className="inline-flex items-center gap-1.5"><span className={MARK_BAD}>{matrix.legend.stale}</span>signed an older revision</span>
          ) : null}
          {matrix.legend.overdue ? (
            <span className="inline-flex items-center gap-1.5"><span className={MARK_BAD}>{matrix.legend.overdue}</span>unsigned, past due</span>
          ) : null}
          {matrix.legend.due ? (
            <span className="inline-flex items-center gap-1.5"><span className={MARK_DUE}>{matrix.legend.due}</span>unsigned, due</span>
          ) : null}
          {matrix.legend.critical ? (
            <span className="inline-flex items-center gap-1.5"><span className="font-mono text-[11px] font-semibold text-ink-primary">{matrix.legend.critical}</span>critical SOP</span>
          ) : null}
        </div>
      </DowntimeCard>
    </Commentable>
  );
}
